import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Output,
  EventEmitter,
  NgZone,
  HostListener,
} from '@angular/core';
import type { PageViewport } from 'pdfjs-dist';
import * as fabric from 'fabric';
import type { TPointerEventInfo, TPointerEvent } from 'fabric';
import type { Pt, StoredAnnotation } from '../pdf-markup-annotation.model';

export type { StoredAnnotation } from '../pdf-markup-annotation.model';
export type RedlineTool = '' | 'arrow' | 'rectangle' | 'ellipse' | 'polygon' | 'cloud' | 'textbox';

interface PageState {
  pdfWidth: number;
  pdfHeight: number;
  annotations: StoredAnnotation[];
}

/** Shared shape for the custom metadata fabric.js objects carry in this component. */
interface RedlineObject extends fabric.Object {
  annotationId?: string;
  annotationType?: string;
  cloudPoints?: Pt[];
  _cloudSkeleton?: boolean;
  _cloudStyle?: { stroke?: string; strokeWidth?: number; fill?: string };
  edit?: boolean;
}

/** Shared in-progress state for the click-to-add-vertex tools (polygon, cloud). */
interface PolylineCtx {
  points: Pt[];
  guides: fabric.Object[];
  preview: fabric.Line | null;
  active: boolean;
}

interface PageHistory {
  undo: StoredAnnotation[][];
  redo: StoredAnnotation[][];
  current: StoredAnnotation[] | null;
}

const CLOUD_BUMP_PDF = 8;
const HISTORY_LIMIT = 50;
const HISTORY_EXTRA_PROPS = ['annotationId', 'annotationType', 'cloudPoints', '_cloudStyle', 'edit'];

@Component({
  selector: 'app-pdf-redline',
  templateUrl: './pdf-redline.component.html',
  styleUrls: ['./pdf-redline.component.scss'],
  standalone: false,
})
export class PdfRedlineComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('redlineCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  @Input() viewport: PageViewport | null = null;
  @Input() page = 1;
  @Input() scale = 1;
  @Input() rotation = 0; // 0 | 90 | 180 | 270
  @Input() activeTool: RedlineTool = '';
  @Input() strokeColor = '#ff0000';
  @Input() fillColor = 'transparent';
  @Input() lineWidth = 1;
  @Input() baseWidth = 0;
  @Input() baseHeight = 0;
  @Input() initialAnnotations: Record<number, StoredAnnotation[]> | null = null;

  @Output() toolDeactivated = new EventEmitter<void>();
  @Output() annotationsChanged = new EventEmitter<void>();
  @Output() annotationDeleted = new EventEmitter<string>();
  @Output() historyChanged = new EventEmitter<{ canUndo: boolean; canRedo: boolean }>();
  @Output() undoRedoApplied = new EventEmitter<void>();
  @Output() activeObjectTypeChange = new EventEmitter<string | null>();

  private historyByPage: Record<number, PageHistory> = {};
  private historyRestoring = false;
  private historyBusy = false;
  private fabricCanvas: fabric.Canvas | null = null;
  private pageData: Record<number, PageState> = {};

  private readonly renderedPages = new Set<number>();

  private rendering = false;
  private editingTransition = false;

  constructor(
    private ngZone: NgZone,
    private hostRef: ElementRef<HTMLElement>,
  ) {}

  // ── lifecycle ──────────────────────────────────────────────────────────────

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.fabricCanvas = new fabric.Canvas(this.canvasRef.nativeElement, { selection: true });
      this.wireCanvasEvents(this.fabricCanvas);
      if (this.initialAnnotations) this.applyInitialAnnotations(this.initialAnnotations);
      this.applyViewport();
      this.applyTool();
      void this.renderStoredAnnotations();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialAnnotations'] && this.initialAnnotations) {
      this.applyInitialAnnotations(this.initialAnnotations);
      if (this.fabricCanvas) {
        this.applyViewport();
        void this.renderStoredAnnotations().then(() => this.historyRebaseline());
      }
    }

    if (!this.fabricCanvas) return;
    if (changes['page'] && !changes['page'].firstChange) {
      this.serializePage(changes['page'].previousValue as number, false);
    }
    if (
      changes['viewport'] ||
      changes['scale'] ||
      changes['rotation'] ||
      changes['baseWidth'] ||
      changes['baseHeight']
    ) {
      this.applyViewport();
      void this.renderStoredAnnotations();
    }
    if (changes['activeTool']) this.applyTool();
    if (changes['lineWidth']) this.applyActiveStrokeWidth();
  }

  private applyInitialAnnotations(byPage: Record<number, StoredAnnotation[]>): void {
    for (const [page, anns] of Object.entries(byPage)) {
      const ps = this.getPage(Number(page));
      ps.annotations = anns;
    }
  }

  getAllAnnotationsByPage(): Record<number, StoredAnnotation[]> {
    if (this.fabricCanvas) this.serializePage(this.page, false);
    const result: Record<number, StoredAnnotation[]> = {};
    for (const [page, ps] of Object.entries(this.pageData)) {
      if (ps.annotations.length) result[Number(page)] = ps.annotations;
    }
    return result;
  }

  private getHistory(page = this.page): PageHistory {
    if (!this.historyByPage[page]) {
      this.historyByPage[page] = { undo: [], redo: [], current: null };
    }
    return this.historyByPage[page];
  }

  private snapshotPage(): StoredAnnotation[] {
    const ps = this.getPage();
    return ps.annotations.map((a) => JSON.parse(JSON.stringify(a)) as StoredAnnotation);
  }

  private notifyHistoryChanged(h: PageHistory): void {
    this.ngZone.run(() => this.historyChanged.emit({ canUndo: h.undo.length > 0, canRedo: h.redo.length > 0 }));
  }

  historyCheckpoint(): void {
    if (!this.fabricCanvas || this.historyRestoring || this.rendering || this.editingTransition) return;
    const h = this.getHistory();
    const snap = this.snapshotPage();
    const snapStr = JSON.stringify(snap);
    if (JSON.stringify(h.current) === snapStr) return;
    if (h.current !== null) {
      h.undo.push(h.current);
      if (h.undo.length > HISTORY_LIMIT) h.undo.shift();
    }
    h.current = snap;
    h.redo = [];
    this.notifyHistoryChanged(h);
  }

  private historyInitPage(): void {
    const h = this.getHistory();
    if (h.current === null) {
      h.current = this.snapshotPage();
    }
    this.notifyHistoryChanged(h);
  }

  historyRebaseline(page = this.page): void {
    const h = this.getHistory(page);
    h.current = this.snapshotPage();
    h.undo = [];
    h.redo = [];
    if (page === this.page) this.notifyHistoryChanged(h);
  }

  private async historyRestore(annotations: StoredAnnotation[]): Promise<void> {
    const fc = this.fabricCanvas;
    if (!fc) return;
    this.historyRestoring = true;
    this.rendering = true;
    try {
      const ps = this.getPage();
      ps.annotations = annotations.map((a) => JSON.parse(JSON.stringify(a)) as StoredAnnotation);
      fc.clear();
      for (const a of ps.annotations) {
        const obj = this.createAnnotationObject(a, ps.pdfWidth, ps.pdfHeight);
        if (!obj) continue;
        fc.add(obj);
        this.finalize(obj);
      }
      fc.discardActiveObject();
      fc.renderAll();
      this.ngZone.run(() => {
        this.annotationsChanged.emit();
        this.undoRedoApplied.emit();
      });
    } finally {
      this.rendering = false;
      this.historyRestoring = false;
    }
  }

  async undo(): Promise<void> {
    if (this.historyBusy) return;
    const h = this.getHistory();
    if (h.undo.length === 0) return;
    this.historyBusy = true;
    try {
      const prev = h.undo.pop()!;
      if (h.current !== null) h.redo.push(h.current);
      h.current = prev;
      await this.historyRestore(prev);
      this.notifyHistoryChanged(h);
    } finally {
      this.historyBusy = false;
    }
  }

  async redo(): Promise<void> {
    if (this.historyBusy) return;
    const h = this.getHistory();
    if (h.redo.length === 0) return;
    this.historyBusy = true;
    try {
      const next = h.redo.pop()!;
      if (h.current !== null) h.undo.push(h.current);
      h.current = next;
      await this.historyRestore(next);
      this.notifyHistoryChanged(h);
    } finally {
      this.historyBusy = false;
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(e: KeyboardEvent): void {
    const isCtrl = e.ctrlKey || e.metaKey;
    if (!isCtrl) return;
    const activeObj = this.fabricCanvas?.getActiveObject() as (fabric.Object & { isEditing?: boolean }) | undefined;
    if (activeObj?.isEditing) return;
    const key = e.key.toLowerCase();
    if (key === 'z' && !e.shiftKey) {
      e.preventDefault();
      if (!this.historyBusy) void this.undo();
    } else if (key === 'y' || (key === 'z' && e.shiftKey)) {
      e.preventDefault();
      if (!this.historyBusy) void this.redo();
    }
  }

  ngOnDestroy(): void {
    this.serializePage(this.page, false);
    this.fabricCanvas?.dispose();
    this.fabricCanvas = null;
  }

  // ── public helpers ────────────────────────────────────────
  setTool(tool: RedlineTool): void {
    this.activeTool = tool;
    this.applyTool();
  }

  clearActiveObject(): void {
    const fc = this.fabricCanvas;
    if (!fc) return;
    const obj = fc.getActiveObject();
    if (!obj) return;
    const id = (obj as RedlineObject).annotationId;
    fc.remove(obj);
    fc.discardActiveObject();
    fc.renderAll();
    this.serializePage(this.page);
    this.historyCheckpoint();
    if (id) this.ngZone.run(() => this.annotationDeleted.emit(id));
  }

  clearAll(): void {
    const ids = (this.fabricCanvas?.getObjects() ?? [])
      .map((obj) => (obj as RedlineObject).annotationId)
      .filter((id): id is string => !!id);
    this.fabricCanvas?.clear();
    const ps = this.getPage();
    ps.annotations = [];
    this.historyRebaseline();
    this.ngZone.run(() => {
      this.annotationsChanged.emit();
      ids.forEach((id) => this.annotationDeleted.emit(id));
    });
  }

  private clampToPage(x: number, y: number): Pt {
    const fc = this.fabricCanvas!;
    return {
      x: Math.max(0, Math.min(x, fc.width!)),
      y: Math.max(0, Math.min(y, fc.height!)),
    };
  }

  private isOutsidePage(x: number, y: number): boolean {
    const fc = this.fabricCanvas!;
    return x < 0 || y < 0 || x > fc.width! || y > fc.height!;
  }

  // ── coordinate math ────────────────────────────────
  private pdfToScreen(v: number): number {
    return (v || 0) * (this.scale || 1);
  }
  private screenToPdf(v: number): number {
    return (v || 0) / (this.scale || 1);
  }
  private normalizeAngle(a: number): number {
    const n = a % 360;
    return n < 0 ? n + 360 : n;
  }

  private toPdfPoint(p: Pt, w: number, h: number): Pt {
    const s = this.scale || 1;
    switch (this.normalizeAngle(this.rotation)) {
      case 90:
        return { x: p.y / s, y: h - p.x / s };
      case 180:
        return { x: w - p.x / s, y: h - p.y / s };
      case 270:
        return { x: w - p.y / s, y: p.x / s };
      default:
        return { x: p.x / s, y: p.y / s };
    }
  }

  private fromPdfPoint(p: Pt, w: number, h: number): Pt {
    const s = this.scale || 1;
    switch (this.normalizeAngle(this.rotation)) {
      case 90:
        return { x: (h - p.y) * s, y: p.x * s };
      case 180:
        return { x: (w - p.x) * s, y: (h - p.y) * s };
      case 270:
        return { x: p.y * s, y: (w - p.x) * s };
      default:
        return { x: p.x * s, y: p.y * s };
    }
  }

  private toNorm(p: Pt, w: number, h: number): Pt {
    const pdf = this.toPdfPoint(p, w, h);
    return { x: w ? pdf.x / w : 0, y: h ? pdf.y / h : 0 };
  }
  private fromNorm(p: Pt, w: number, h: number): Pt {
    return this.fromPdfPoint({ x: p.x * w, y: p.y * h }, w, h);
  }

  // ── page state ────────────────────────────────────────────────────────────────

  private emptyPage(): PageState {
    return { pdfWidth: 0, pdfHeight: 0, annotations: [] };
  }
  private getPage(n = this.page): PageState {
    if (!this.pageData[n]) this.pageData[n] = this.emptyPage();
    return this.pageData[n];
  }

  // ── viewport / canvas resize ──────────────────────────────────────────────────

  private applyViewport(): void {
    if (!this.fabricCanvas || !this.viewport) return;
    this.fabricCanvas.setDimensions({ width: this.viewport.width, height: this.viewport.height });
    // Record base (unscaled) page dims from current viewport
    if (this.baseWidth && this.baseHeight) {
      const ps = this.getPage(this.page);
      ps.pdfWidth = this.baseWidth;
      ps.pdfHeight = this.baseHeight;
    }

    this.fabricCanvas.renderAll();
  }

  // ── tool switching ──────────────────────────────────

  private applyTool(): void {
    const fc = this.fabricCanvas;
    if (!fc) return;

    this.ngZone.runOutsideAngular(() => {
      fc.isDrawingMode = false;
      fc.off('mouse:down');
      fc.off('mouse:move');
      fc.off('mouse:up');
      fc.off('mouse:dblclick');

      const isDrawingTool = this.activeTool !== '';
      fc.selection = !isDrawingTool;
      fc.defaultCursor = isDrawingTool ? 'crosshair' : 'default';
      fc.renderAll();

      switch (this.activeTool) {
        case 'arrow':
          this.toolArrow(fc);
          break;
        case 'rectangle':
          this.toolShape(fc, 'rectangle');
          break;
        case 'ellipse':
          this.toolShape(fc, 'ellipse');
          break;
        case 'polygon':
          this.toolPolygon(fc);
          break;
        case 'cloud':
          this.toolCloud(fc);
          break;
        case 'textbox':
          this.toolTextbox(fc);
          break;
        default:
          break;
      }
    });
  }

  private deactivateTool(): void {
    this.activeTool = '';
    this.applyTool();
    this.ngZone.run(() => this.toolDeactivated.emit());
  }

  // ── canvas persistence events ─────────────────────────────────────────────────

  private wireCanvasEvents(fc: fabric.Canvas): void {
    fc.on('object:modified', ({ target }) => {
      target?.setCoords();
      if ((target as RedlineObject)?._cloudSkeleton || (target as RedlineObject)?.edit) return;
      setTimeout(() => {
        if (this.rendering || this.historyRestoring) return;
        this.serializePage(this.page);
        this.historyCheckpoint();
      });
    });
    fc.on('object:added', () => this.serializePage(this.page));
    fc.on('object:removed', () => this.serializePage(this.page));
    fc.on('text:editing:exited', ({ target }) => {
      target?.setCoords();
      setTimeout(() => {
        if (this.rendering || this.historyRestoring) return;
        this.serializePage(this.page);
        this.historyCheckpoint();
      });
    });
    fc.on('selection:created', ({ selected }: { selected: fabric.Object[] }) => {
      if (this.editingTransition) return;
      (selected || []).forEach((obj) => this.enterVertexEditing(obj));
      this.ngZone.run(() => this.activeObjectTypeChange.emit(selected?.[0]?.type ?? null));
    });
    fc.on(
      'selection:updated',
      ({ selected, deselected }: { selected: fabric.Object[]; deselected: fabric.Object[] }) => {
        if (this.editingTransition) return;
        (deselected || []).forEach((obj) => this.commitVertexEditing(obj));
        this.ngZone.run(() => this.activeObjectTypeChange.emit(selected?.[0]?.type ?? null));
      },
    );
    fc.on('selection:cleared', ({ deselected }: { deselected: fabric.Object[] }) => {
      if (this.editingTransition) return;
      (deselected || []).forEach((obj) => this.commitVertexEditing(obj));
      this.ngZone.run(() => this.activeObjectTypeChange.emit(null));
    });
  }

  private finalize(obj: fabric.Object, makeActive = false): void {
    obj.set({ selectable: true, evented: true });
    if (obj.type === 'polygon' && !(obj as RedlineObject)._cloudSkeleton) {
      this.applyPolyEditControls(obj as fabric.Polygon);
    }
    obj.setCoords();
    if (makeActive) this.fabricCanvas?.setActiveObject(obj);
  }

  private applyActiveStrokeWidth(): void {
    const fc = this.fabricCanvas;
    const obj = fc?.getActiveObject() as RedlineObject | undefined;
    if (!obj) return;
    const strokeWidth = this.pdfToScreen(this.lineWidth);

    if (obj.annotationType === 'arrow' && obj instanceof fabric.Group) {
      const shaft = obj.item(0) as fabric.Line | undefined;
      const head = obj.item(1) as fabric.Triangle | undefined;
      shaft?.set('strokeWidth', strokeWidth);
      head?.set({ width: strokeWidth * (14 / 3), height: strokeWidth * 6 });
      obj.triggerLayout();
      obj.setCoords();
      fc!.requestRenderAll();
      this.serializePage(this.page);
      this.historyCheckpoint();
      return;
    }

    if (!('strokeWidth' in obj) || obj.type === 'textbox') return;

    obj.set('strokeWidth', strokeWidth);
    obj.setCoords();
    fc!.requestRenderAll();
    this.serializePage(this.page);
    this.historyCheckpoint();
  }

  // ── Tool: Arrow ───────────────────────────────────────────────────────────────

  private toolArrow(fc: fabric.Canvas): void {
    let line: fabric.Line | null = null;
    let sx = 0;
    let sy = 0;
    let drawing = false;

    fc.on('mouse:down', ({ e }: TPointerEventInfo<TPointerEvent>) => {
      const raw = fc.getScenePoint(e);
      if (this.isOutsidePage(raw.x, raw.y)) return;
      drawing = true;
      const p = this.clampToPage(raw.x, raw.y);
      sx = p.x;
      sy = p.y;
      line = new fabric.Line([sx, sy, sx, sy], {
        stroke: this.strokeColor,
        strokeWidth: this.pdfToScreen(this.lineWidth),
        strokeUniform: true,
        selectable: false,
      });
      fc.add(line);
    });

    fc.on('mouse:move', ({ e }: TPointerEventInfo<TPointerEvent>) => {
      if (!drawing || !line) return;
      const raw = fc.getScenePoint(e);
      const { x, y } = this.clampToPage(raw.x, raw.y);
      line.set({ x2: x, y2: y });
      fc.renderAll();
    });

    fc.on('mouse:up', ({ e }: TPointerEventInfo<TPointerEvent>) => {
      if (!drawing) return;
      drawing = false;
      const raw = fc.getScenePoint(e);
      const { x, y } = this.clampToPage(raw.x, raw.y);
      if (line) fc.remove(line);
      const group = this.createArrowGroup(
        { x: sx, y: sy },
        { x, y },
        this.strokeColor,
        this.pdfToScreen(this.lineWidth),
      );
      fc.add(group);
      group.setCoords();
      fc.setActiveObject(group);
      fc.renderAll();
      this.serializePage(this.page);
      this.historyCheckpoint();
      this.deactivateTool();
    });
  }

  private createArrowGroup(start: Pt, end: Pt, stroke: string, strokeWidth: number): fabric.Group {
    const angle = (Math.atan2(end.y - start.y, end.x - start.x) * 180) / Math.PI;
    const shaft = new fabric.Line([start.x, start.y, end.x, end.y], {
      stroke,
      strokeWidth,
      originX: 'center',
      originY: 'center',
    });
    const head = new fabric.Triangle({
      width: strokeWidth * (14 / 3),
      height: strokeWidth * 6,
      fill: stroke,
      left: end.x,
      top: end.y,
      originX: 'center',
      originY: 'center',
      angle: angle + 90,
    });
    const group = new fabric.Group([shaft, head], { selectable: true, evented: true });
    (group as RedlineObject).annotationType = 'arrow';
    return group;
  }

  // ── Tool: Rectangle / Ellipse ─────────────────────────────────────────────────

  private toolShape(fc: fabric.Canvas, type: 'rectangle' | 'ellipse'): void {
    let shape: fabric.Rect | fabric.Ellipse | null = null;
    let sx = 0;
    let sy = 0;
    let drawing = false;

    fc.on('mouse:down', ({ e }: TPointerEventInfo<TPointerEvent>) => {
      const raw = fc.getScenePoint(e);
      if (this.isOutsidePage(raw.x, raw.y)) return;
      drawing = true;
      const p = this.clampToPage(raw.x, raw.y);
      sx = p.x;
      sy = p.y;
      const base = {
        left: sx,
        top: sy,
        width: 0,
        height: 0,
        originX: 'center' as const,
        originY: 'center' as const,
        stroke: this.strokeColor,
        strokeWidth: this.pdfToScreen(this.lineWidth),
        strokeUniform: true,
        fill: this.fillColor,
        selectable: false,
      };
      shape = type === 'ellipse' ? new fabric.Ellipse({ ...base, rx: 0, ry: 0 }) : new fabric.Rect(base);
      fc.add(shape);
    });

    fc.on('mouse:move', ({ e }: TPointerEventInfo<TPointerEvent>) => {
      if (!drawing || !shape) return;
      const raw = fc.getScenePoint(e);
      const { x, y } = this.clampToPage(raw.x, raw.y);
      const w = Math.abs(x - sx);
      const h = Math.abs(y - sy);
      shape.set({ left: (sx + x) / 2, top: (sy + y) / 2, width: w, height: h });
      if (type === 'ellipse') (shape as fabric.Ellipse).set({ rx: w / 2, ry: h / 2 });
      fc.renderAll();
    });

    fc.on('mouse:up', () => {
      if (shape) {
        this.finalize(shape, true);
        this.serializePage(this.page);
        this.historyCheckpoint();
        this.deactivateTool();
      }
      drawing = false;
      shape = null;
    });
  }

  // ── Tool: Polygon / Cloud (shared click-to-add-vertex flow) ──────────────────

  private toolPolygon(fc: fabric.Canvas): void {
    this.toolPolyline(fc, (points) =>
      points.length >= 3
        ? new fabric.Polygon(points, {
            stroke: this.strokeColor,
            strokeWidth: this.pdfToScreen(this.lineWidth),
            strokeUniform: true,
            fill: this.fillColor,
            selectable: true,
          })
        : null,
    );
  }

  private toolCloud(fc: fabric.Canvas): void {
    this.toolPolyline(fc, (points) =>
      points.length > 2
        ? this.buildCloudPath(points, this.strokeColor, this.fillColor, this.pdfToScreen(this.lineWidth))
        : null,
    );
  }

  private toolPolyline(fc: fabric.Canvas, buildShape: (points: Pt[]) => fabric.Object | null): void {
    const ctx: PolylineCtx = { points: [], guides: [], preview: null, active: false };
    let finishing = false;
    let lastDownTime = 0;
    let lastDownPoint: Pt | null = null;
    const DOUBLE_TAP_MS = 400;
    const DOUBLE_TAP_DIST = 15;

    const finish = () => {
      if (finishing) return;
      finishing = true;
      const shape = buildShape(ctx.points);
      this.clearPolylineGuides(fc, ctx);
      if (shape) {
        fc.add(shape);
        this.finalize(shape, false);
        this.serializePage(this.page);
        this.historyCheckpoint();
      }
      fc.renderAll();
      this.deactivateTool();
    };

    fc.on('mouse:down', ({ e }: TPointerEventInfo<TPointerEvent>) => {
      if (finishing) return;
      const raw = fc.getScenePoint(e);
      if (this.isOutsidePage(raw.x, raw.y)) return;
      const { x, y } = this.clampToPage(raw.x, raw.y);

      const now = Date.now();
      const isNativeDoubleClick = (e as MouseEvent).detail === 2;
      const isDoubleTap =
        ctx.points.length > 0 &&
        lastDownPoint !== null &&
        now - lastDownTime < DOUBLE_TAP_MS &&
        Math.hypot(x - lastDownPoint.x, y - lastDownPoint.y) < DOUBLE_TAP_DIST;

      if (isNativeDoubleClick || isDoubleTap) {
        finish();
        return;
      }

      lastDownTime = now;
      lastDownPoint = { x, y };
      ctx.active = true;
      this.addPolylineVertex(fc, ctx, x, y);
    });

    fc.on('mouse:move', ({ e }: TPointerEventInfo<TPointerEvent>) => this.updatePolylinePreview(fc, ctx, e));
    fc.on('mouse:dblclick', finish);
  }

  private addPolylineVertex(fc: fabric.Canvas, ctx: PolylineCtx, x: number, y: number): void {
    if (ctx.points.length > 0) {
      const prev = ctx.points[ctx.points.length - 1];
      const seg = new fabric.Line([prev.x, prev.y, x, y], {
        stroke: this.strokeColor,
        strokeWidth: this.lineWidth,
        strokeDashArray: [5, 5],
        selectable: false,
        evented: false,
      });
      ctx.guides.push(seg);
      fc.add(seg);
    }
    ctx.points.push({ x, y });
    const dot = new fabric.Circle({
      left: x - 4,
      top: y - 4,
      radius: 4,
      originX: 'left',
      originY: 'top',
      fill: this.strokeColor,
      selectable: false,
    });
    ctx.guides.push(dot);
    fc.add(dot);
    if (ctx.preview) {
      fc.remove(ctx.preview);
      ctx.preview = null;
    }
    fc.renderAll();
  }

  private updatePolylinePreview(fc: fabric.Canvas, ctx: PolylineCtx, e: TPointerEvent): void {
    if (!ctx.active || ctx.points.length === 0) return;
    const raw = fc.getScenePoint(e);
    const { x, y } = this.clampToPage(raw.x, raw.y);
    const last = ctx.points[ctx.points.length - 1];
    if (ctx.preview) fc.remove(ctx.preview);
    ctx.preview = new fabric.Line([last.x, last.y, x, y], {
      stroke: this.strokeColor,
      strokeWidth: this.lineWidth,
      strokeDashArray: [5, 5],
      selectable: false,
    });
    fc.add(ctx.preview);
    fc.renderAll();
  }

  private clearPolylineGuides(fc: fabric.Canvas, ctx: PolylineCtx): void {
    ctx.guides.forEach((g) => fc.remove(g));
    if (ctx.preview) fc.remove(ctx.preview);
    ctx.points = [];
    ctx.guides = [];
    ctx.preview = null;
    ctx.active = false;
  }

  private buildCloudPath(polygonPoints: Pt[], stroke: string, fill: string, strokeWidth: number): fabric.Path {
    const scale = this.scale || 1;
    const BUMP_DIAM_PDF = CLOUD_BUMP_PDF * 2;

    let area = 0;
    for (let k = 0; k < polygonPoints.length; k++) {
      const n = (k + 1) % polygonPoints.length;
      area += (polygonPoints[n].x - polygonPoints[k].x) * (polygonPoints[n].y + polygonPoints[k].y);
    }
    const sweep = area > 0 ? 0 : 1;

    const pathData = [`M ${polygonPoints[0].x} ${polygonPoints[0].y}`];
    for (let i = 0; i < polygonPoints.length; i++) {
      const p1 = polygonPoints[i];
      const p2 = polygonPoints[(i + 1) % polygonPoints.length];
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const numBumps = Math.max(1, Math.round(dist / scale / BUMP_DIAM_PDF));
      const arcR = dist / (numBumps * 2);
      for (let j = 1; j <= numBumps; j++) {
        const t = j / numBumps;
        pathData.push(`A ${arcR} ${arcR} 0 0 ${sweep} ${p1.x + t * dx} ${p1.y + t * dy}`);
      }
    }
    pathData.push('Z');

    const cloud = new fabric.Path(pathData.join(' '), {
      fill: fill || 'transparent',
      stroke: stroke || 'red',
      strokeWidth: strokeWidth || 1.5,
      strokeUniform: true,
      selectable: true,
      objectCaching: false,
    });
    const c = cloud as RedlineObject;
    c.annotationType = 'cloud';
    c.cloudPoints = polygonPoints.map((p) => ({ x: p.x, y: p.y }));
    return cloud;
  }

  // ── Vertex editing ────────────────────────────────────────────────────────────

  private getAbsolutePolygonPoints(polygon: fabric.Polygon): Pt[] {
    const matrix = polygon.calcTransformMatrix();
    return polygon.points.map((point) => {
      const abs = fabric.util.transformPoint(
        new fabric.Point(point.x - polygon.pathOffset.x, point.y - polygon.pathOffset.y),
        matrix,
      );
      return { x: abs.x, y: abs.y };
    });
  }

  private getAbsoluteCloudPoints(cloud: fabric.Path): Pt[] {
    const c = cloud as RedlineObject;
    if (!Array.isArray(c.cloudPoints)) return [];
    const matrix = cloud.calcTransformMatrix();
    const offset: Pt = cloud.pathOffset ?? { x: 0, y: 0 };
    return c.cloudPoints.map((point) => {
      const abs = fabric.util.transformPoint(new fabric.Point(point.x - offset.x, point.y - offset.y), matrix);
      return { x: abs.x, y: abs.y };
    });
  }

  private enterVertexEditing(obj: fabric.Object): void {
    if (!obj || this.activeTool !== '' || this.editingTransition) return;
    const anyObj = obj as RedlineObject;
    if (anyObj.annotationType === 'cloud' && !anyObj._cloudSkeleton) {
      this.enterCloudEditing(obj as fabric.Path);
    }
  }

  private commitVertexEditing(obj: fabric.Object): void {
    if (!obj) return;
    const anyObj = obj as RedlineObject;
    if (anyObj._cloudSkeleton) {
      this.exitCloudEditing(obj as fabric.Polygon);
    }
  }

  private applyPolyEditControls(polygon: fabric.Polygon): void {
    polygon.controls = {
      ...fabric.controlsUtils.createPolyControls(polygon),
      ...fabric.controlsUtils.createObjectDefaultControls(),
    };
    polygon.cornerStyle = 'rect';
    polygon.cornerColor = 'rgb(178,204,255)';
    polygon.cornerSize = 10;
    polygon.hasBorders = true;
    polygon.objectCaching = false;
    polygon.setCoords();
  }

  private enterCloudEditing(cloud: fabric.Path): void {
    const fc = this.fabricCanvas;
    const anyCloud = cloud as RedlineObject;
    if (!fc || !Array.isArray(anyCloud.cloudPoints) || anyCloud.cloudPoints.length < 3) return;
    const absolutePoints = this.getAbsoluteCloudPoints(cloud);
    if (absolutePoints.length < 3) return;

    const skeleton = new fabric.Polygon(
      absolutePoints.map((p) => ({ x: p.x, y: p.y })),
      {
        stroke: cloud.stroke as string,
        strokeWidth: cloud.strokeWidth,
        strokeDashArray: [4, 4],
        fill: 'rgba(0,0,0,0.001)',
        objectCaching: false,
      },
    ) as fabric.Polygon & RedlineObject;
    skeleton._cloudSkeleton = true;
    skeleton.annotationId = anyCloud.annotationId;
    skeleton._cloudStyle = {
      stroke: cloud.stroke as string,
      strokeWidth: cloud.strokeWidth,
      fill: cloud.fill as string,
    };

    this.editingTransition = true;
    try {
      fc.remove(cloud);
      fc.add(skeleton);
      fc.setActiveObject(skeleton);
    } finally {
      this.editingTransition = false;
    }

    this.applyPolyEditControls(skeleton);
    skeleton.setCoords();
  }

  private exitCloudEditing(skeleton: fabric.Polygon): void {
    const fc = this.fabricCanvas;
    const anySkeleton = skeleton as RedlineObject;
    if (!fc || !anySkeleton._cloudSkeleton) return;

    const absPoints = this.getAbsolutePolygonPoints(skeleton);
    if (absPoints.length < 3) {
      fc.remove(skeleton);
      return;
    }

    const style = anySkeleton._cloudStyle || {};
    let rebuiltCloud: fabric.Path | null = null;

    this.editingTransition = true;
    try {
      fc.remove(skeleton);
      rebuiltCloud = this.buildCloudPath(
        absPoints,
        (skeleton.stroke as string) || style.stroke || this.strokeColor,
        style.fill || 'transparent',
        skeleton.strokeWidth || style.strokeWidth || this.pdfToScreen(this.lineWidth),
      );
      const rebuiltAny = rebuiltCloud as RedlineObject;
      if (anySkeleton.annotationId) rebuiltAny.annotationId = anySkeleton.annotationId;
      fc.add(rebuiltCloud);
      this.finalize(rebuiltCloud, false); // do NOT set active here — we're mid selection-event
      fc.requestRenderAll();
    } finally {
      this.editingTransition = false;
    }

    this.serializePage(this.page);
    this.historyCheckpoint();
    const target = rebuiltCloud;
    if (target) {
      setTimeout(() => {
        const canvas = this.fabricCanvas;
        if (!canvas) return;
        if (canvas.getObjects().includes(target) && !canvas.getActiveObject()) {
          this.editingTransition = true;
          try {
            canvas.setActiveObject(target);
            canvas.requestRenderAll();
          } finally {
            this.editingTransition = false;
          }
        }
      }, 0);
    }
  }

  // ── Tool: Textbox ─────────────────────────────────────────────────────────────

  private toolTextbox(fc: fabric.Canvas): void {
    fc.once('mouse:down', ({ e }: TPointerEventInfo<TPointerEvent>) => {
      const raw = fc.getScenePoint(e);
      if (this.isOutsidePage(raw.x, raw.y)) return;
      const { x, y } = this.clampToPage(raw.x, raw.y);
      const tb = new fabric.Textbox('Text', {
        left: x,
        top: y,
        width: this.pdfToScreen(160),
        fontSize: this.pdfToScreen(14),
        fill: this.strokeColor,
        selectable: true,
        editable: true,
        originX: 'left',
        originY: 'top',
      });
      tb.hiddenTextareaContainer = this.hostRef.nativeElement;
      fc.add(tb);
      this.finalize(tb, true);
      tb.enterEditing();
      fc.renderAll();
      this.serializePage(this.page);
      this.deactivateTool();
    });
  }

  // ── Serialize (retain coords like JS) ─────────────────────────────────────────

  private serializePage(pageNum: number, notify = true): void {
    if (!this.fabricCanvas || this.rendering || this.editingTransition) return;
    if (!this.renderedPages.has(pageNum)) return;
    const ps = this.getPage(pageNum);
    if (!ps.pdfWidth || !ps.pdfHeight) return;
    ps.annotations = this.fabricCanvas
      .getObjects()
      .map((obj) => this.serializeAnnotation(obj, ps.pdfWidth, ps.pdfHeight))
      .filter((a): a is StoredAnnotation => a !== null);
    if (notify) this.annotationsChanged.emit();
  }

  private ensureAnnotationId(obj: fabric.Object): string {
    const anyObj = obj as RedlineObject;
    if (!anyObj.annotationId) anyObj.annotationId = this.genAnnotationId();
    return anyObj.annotationId;
  }

  private genAnnotationId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private serializeAnnotation(obj: fabric.Object, w: number, h: number): StoredAnnotation | null {
    if (!obj.selectable) return null;
    const anyObj = obj as RedlineObject;
    if (anyObj._cloudSkeleton) return null;
    const id = this.ensureAnnotationId(obj);
    let result: StoredAnnotation | null = null;

    if (anyObj.annotationType === 'arrow' && obj instanceof fabric.Group) {
      const shaft = obj.item(0) as fabric.Line;
      const m = shaft.calcTransformMatrix();
      const lp = shaft.calcLinePoints();
      const s = fabric.util.transformPoint(new fabric.Point(lp.x1, lp.y1), m);
      const e = fabric.util.transformPoint(new fabric.Point(lp.x2, lp.y2), m);
      result = {
        type: 'arrow',
        start: this.toNorm({ x: s.x, y: s.y }, w, h),
        end: this.toNorm({ x: e.x, y: e.y }, w, h),
        stroke: shaft.stroke as string,
        strokeWidth: (shaft.strokeWidth || this.lineWidth) / this.scale,
      };
    } else if (obj.type === 'rect') {
      result = this.serializeBox(obj, 'rectangle', w, h);
    } else if (obj.type === 'ellipse') {
      result = this.serializeBox(obj, 'ellipse', w, h);
    } else if (obj.type === 'polygon' && !anyObj.cloudPoints) {
      const poly = obj as fabric.Polygon;
      const m = poly.calcTransformMatrix();
      const pts = poly.points.map((p) => {
        const abs = fabric.util.transformPoint(new fabric.Point(p.x - poly.pathOffset.x, p.y - poly.pathOffset.y), m);
        return this.toNorm({ x: abs.x, y: abs.y }, w, h);
      });
      result = {
        type: 'polygon',
        points: pts,
        stroke: obj.stroke as string,
        strokeWidth: (obj.strokeWidth || this.lineWidth) / this.scale,
        fill: obj.fill as string,
      };
    } else if (anyObj.cloudPoints && Array.isArray(anyObj.cloudPoints)) {
      const m = obj.calcTransformMatrix();
      const offset = (obj as fabric.Path).pathOffset || { x: 0, y: 0 };
      const pts = anyObj.cloudPoints.map((p) => {
        const abs = fabric.util.transformPoint(new fabric.Point(p.x - offset.x, p.y - offset.y), m);
        return this.toNorm({ x: abs.x, y: abs.y }, w, h);
      });
      result = {
        type: 'cloud',
        center: this.toNorm(obj.getCenterPoint(), w, h),
        points: pts,
        stroke: obj.stroke as string,
        strokeWidth: (obj.strokeWidth || this.lineWidth) / this.scale,
        fill: (obj.fill as string) || 'transparent',
      };
    } else if (obj.type === 'textbox') {
      const tb = obj as fabric.Textbox;
      result = {
        type: 'textbox',
        center: this.toNorm(obj.getCenterPoint(), w, h),
        width: w ? this.screenToPdf(tb.width * (tb.scaleX || 1)) / w : 0,
        fontSize: this.screenToPdf((tb.fontSize || 14) * (tb.scaleY || 1)),
        angle: this.normalizeAngle((obj.angle || 0) - this.rotation),
        fill: obj.fill as string,
        text: tb.text,
      };
    }

    if (result) result.id = id;
    return result;
  }

  private serializeBox(obj: fabric.Object, type: string, w: number, h: number): StoredAnnotation {
    return {
      type,
      center: this.toNorm(obj.getCenterPoint(), w, h),
      width: w ? this.screenToPdf(obj.width * (obj.scaleX || 1)) / w : 0,
      height: h ? this.screenToPdf(obj.height * (obj.scaleY || 1)) / h : 0,
      angle: this.normalizeAngle((obj.angle || 0) - this.rotation),
      stroke: obj.stroke as string,
      strokeWidth: this.screenToPdf(obj.strokeWidth || this.pdfToScreen(this.lineWidth)),
      fill: obj.fill as string,
    };
  }

  // ── Deserialize / render stored ───────────────────────────────────────────────

  private async renderStoredAnnotations(): Promise<void> {
    const fc = this.fabricCanvas;
    if (!fc) return;
    const ps = this.getPage(this.page);
    if (!ps.pdfWidth || !ps.pdfHeight) return;
    this.rendering = true;
    try {
      fc.clear();
      for (const a of ps.annotations) {
        const obj = this.createAnnotationObject(a, ps.pdfWidth, ps.pdfHeight);
        if (!obj) continue;
        fc.add(obj);
        this.finalize(obj);
      }
      fc.discardActiveObject();
      fc.renderAll();
      this.renderedPages.add(this.page);
      this.historyInitPage();
    } finally {
      this.rendering = false;
    }
  }

  private createAnnotationObject(a: StoredAnnotation, w: number, h: number): fabric.Object | null {
    const obj = this.buildAnnotationObject(a, w, h);
    if (obj && a.id) (obj as RedlineObject).annotationId = a.id;
    return obj;
  }

  private buildAnnotationObject(a: StoredAnnotation, w: number, h: number): fabric.Object | null {
    switch (a.type) {
      case 'arrow': {
        const start = this.fromNorm(a.start!, w, h);
        const end = this.fromNorm(a.end!, w, h);
        return this.createArrowGroup(
          start,
          end,
          a.stroke || this.strokeColor,
          (a.strokeWidth || this.lineWidth) * this.scale,
        );
      }
      case 'rectangle': {
        const { width, height, common } = this.boxBaseProps(a, w, h);
        return new fabric.Rect({ ...common, width, height });
      }
      case 'ellipse': {
        const { width, height, common } = this.boxBaseProps(a, w, h);
        return new fabric.Ellipse({ ...common, rx: width / 2, ry: height / 2 });
      }
      case 'polygon': {
        const pts = a.points!.map((p) => this.fromNorm(p, w, h));
        return new fabric.Polygon(pts, {
          stroke: a.stroke,
          strokeWidth: (a.strokeWidth || this.lineWidth) * this.scale,
          strokeUniform: true,
          fill: a.fill,
        });
      }
      case 'cloud': {
        if (a.points && a.points.length > 2) {
          const pts = a.points.map((p) => this.fromNorm(p, w, h));
          return this.buildCloudPath(
            pts,
            a.stroke || this.strokeColor,
            a.fill || 'transparent',
            (a.strokeWidth || this.lineWidth) * this.scale,
          );
        }
        return null;
      }
      case 'textbox': {
        const center = this.fromNorm(a.center!, w, h);
        const tb = new fabric.Textbox(a.text || 'Text', {
          left: center.x,
          top: center.y,
          originX: 'center',
          originY: 'center',
          width: (a.width || 0) * w * this.scale,
          fontSize: (a.fontSize || 16) * this.scale,
          angle: this.normalizeAngle((a.angle || 0) + this.rotation),
          fill: a.fill,
          editable: true,
        });
        tb.hiddenTextareaContainer = this.hostRef.nativeElement;
        return tb;
      }
      default:
        return null;
    }
  }

  private boxBaseProps(a: StoredAnnotation, w: number, h: number) {
    const center = this.fromNorm(a.center!, w, h);
    return {
      width: this.pdfToScreen((a.width || 0) * w),
      height: this.pdfToScreen((a.height || 0) * h),
      common: {
        left: center.x,
        top: center.y,
        originX: 'center' as const,
        originY: 'center' as const,
        angle: this.normalizeAngle((a.angle || 0) + this.rotation),
        stroke: a.stroke,
        strokeWidth: this.pdfToScreen(a.strokeWidth || this.lineWidth),
        strokeUniform: true,
        fill: a.fill,
      },
    };
  }
}
